import React from 'react';
import { InferProps } from 'prop-types';
import {
  useForm,
  SubmitHandler,
  FormProvider,
} from 'react-hook-form';
import { Box } from '@chakra-ui/react';

import { FormInputsType, PaymentElementResponse } from '../../../@types';
import { sanitizeErrors } from '../../../utils/errors';
import { useTranslations } from '../../../translations/useTranslations';
import { useCommonOptionsStore } from '../../../store';
import { CommonPaymentElementPropTypes } from '../../../propTypes';

export const AffirmFormPropTypes = {
  ...CommonPaymentElementPropTypes,
};
export type AffirmFormProps = InferProps<typeof AffirmFormPropTypes>;

const AffirmForm = React.forwardRef(({
  onLoadStart,
  onLoadEnd,
  onSuccess,
  onError,
  onSubmit,
}: AffirmFormProps, forwardRef: any) => {
  const lang = useCommonOptionsStore(state => state.lang);
  const [t] = useTranslations(lang);

  const formMethods = useForm<FormInputsType>({
    mode: 'onChange',
  });

  const { handleSubmit, formState } = formMethods;

  const {
    errors,
    isLoading,
    isSubmitSuccessful,
    isSubmitting,
  } = formState;

  const errCount = React.useMemo(() => (errors ? Object.keys(errors).length : 0), [errors]);

  React.useEffect(() => {
    if (onLoadStart && isLoading) onLoadStart();
    if (onLoadEnd && isLoading) onLoadEnd();
  }, [isLoading, onLoadStart, onLoadEnd]);

  React.useEffect(() => {
    if (onLoadStart && isSubmitting) onLoadStart();
    if (onLoadEnd && isSubmitting) onLoadEnd();
  }, [isSubmitting, onLoadStart, onLoadEnd]);

  React.useEffect(() => {
    if (onSuccess && isSubmitSuccessful) onSuccess();
  }, [isSubmitSuccessful, onSuccess]);

  React.useEffect(() => {
    if (onError && errCount) onError(sanitizeErrors(errors));
  }, [onError, errors, errCount]);

  const layoutProps: Record<string, any> = {
    gap: 6,
    columnGap: 4,
  };

  const submitHandler: SubmitHandler<FormInputsType> = async (formData: any) => {
    const data = { ...formData };

    if (onSubmit) {
      const responseData: PaymentElementResponse = {
        ...data,
        paymentMethod: 'affirm',
      };

      onSubmit({ ...responseData });
    }
  };

  const submitErrorHandler: SubmitHandler<FormInputsType> = async () => {
    if (onError) onError(sanitizeErrors(errors));
  };

  React.useImperativeHandle(forwardRef, () => ({
    submit() {
      handleSubmit(submitHandler, submitErrorHandler)();
    },
    async validate() {
      return true;
    },
  }));

  return (
    <>
      <FormProvider {...formMethods}>
        <Box paddingTop={(layoutProps as any).gap}>
          {t('affirmMessage')}
        </Box>
      </FormProvider>
    </>
  );
});

AffirmForm.propTypes = AffirmFormPropTypes;

export { AffirmForm };
